import { Node as ReactFlowNode } from '@xyflow/react';
import { LogOut, Send, Target, Users, Zap, Loader2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { ICampaign } from 'types';
import { cn } from '../../utils/cn';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import TriggerConditionDisplay from './TriggerConditionDisplay';

interface ReviewCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartCampaign: (selectedType?: 'all_contacts' | 'future_contacts') => void;
  campaign: ICampaign;
  nodes: ReactFlowNode[];
  loading?: boolean;
}

interface ReviewSectionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  additionalText?: string;
  onEdit: () => void;
  children?: React.ReactNode;
}

const ReviewSection: React.FC<ReviewSectionProps> = ({
  icon,
  title,
  description,
  additionalText,
  onEdit,
  children,
}) => {
  return (
    <div className="flex items-start space-x-3 p-3 border border-border rounded-lg bg-muted/30">
      <div className="flex-shrink-0 w-6 h-6 bg-success-background rounded-md flex items-center justify-center text-success">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          <Button
            variant="ghost"
            size="small"
            onClick={onEdit}
            className="text-primary hover:text-primary-hover text-xs px-2 py-1 h-auto"
          >
            Edit
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mb-1">{description}</p>
        {additionalText && (
          <p className="text-xs text-muted-foreground mb-2">{additionalText}</p>
        )}
        {children}
      </div>
    </div>
  );
};

const ReviewCampaignModal: React.FC<ReviewCampaignModalProps> = ({
  isOpen,
  onClose,
  onStartCampaign,
  campaign,
  nodes,
  loading = false,
}) => {
  const [includeExistingMatches, setIncludeExistingMatches] = useState(true);
  const [triggerConditions, setTriggerConditions] = useState<any>(null);
  const [triggerType, setTriggerType] = useState<'event' | 'attribute-segment'>(
    'attribute-segment'
  );
  const [triggerEventName, setTriggerEventName] = useState('');

  useEffect(() => {
    if (nodes) {
      const triggerNode = nodes.find(
        (node: ReactFlowNode) => node.type === 'trigger'
      );
      if (triggerNode) {
        const config = (triggerNode.data as any)?.config || {};
        if (config.conditions) {
          setTriggerConditions(config.conditions);
        }
        setTriggerType(
          config.trigger === 'event' ? 'event' : 'attribute-segment'
        );
        setTriggerEventName(config.event || '');
      }
    }
  }, [nodes]);

  const handleStartCampaign = () => {
    const selectedType = includeExistingMatches
      ? 'all_contacts'
      : 'future_contacts';
    onStartCampaign(selectedType);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Review and start campaign"
      size="large"
    >
      <div className="space-y-4">
        {/* Subtitle */}
        <p className="text-xs text-gray-500 mb-4">
          Review your campaign before starting{' '}
          {campaign?.name || 'Untitled Campaign'}.
        </p>

        {/* Trigger Section */}
        <ReviewSection
          icon={<Zap className="w-4 h-4 text-success" />}
          title="Trigger"
          description={
            triggerType === 'event'
              ? 'People will enter this campaign every time they perform the event:'
              : 'People will enter this campaign the first time they meet the following conditions:'
          }
          onEdit={() => {
            // TODO: Implement edit trigger functionality
            console.log('Edit trigger');
          }}
        >
          <div className="space-y-3">
            <TriggerConditionDisplay
              conditions={triggerConditions || {}}
              triggerType={triggerType}
              eventName={triggerEventName}
              variant="compact"
            />

            {triggerType !== 'event' && (
              <div>
                <p className="text-xs text-gray-600 mb-2">
                  Would you like your campaign to include existing matches, or
                  start fresh?
                </p>

                <div className="flex gap-2">
                  <div
                    className={cn(
                      'p-2 border rounded-md cursor-pointer transition-colors',
                      includeExistingMatches
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:bg-muted/50'
                    )}
                    onClick={() => setIncludeExistingMatches(true)}
                  >
                    <div className="flex items-start space-x-2">
                      <input
                        type="radio"
                        checked={includeExistingMatches}
                        onChange={() => setIncludeExistingMatches(true)}
                        className="mt-0.5 accent-primary border-border focus:ring-0"
                      />
                      <div>
                        <p className="text-xs font-medium text-foreground">
                          Current people and future additions
                        </p>
                        <p className="text-xs text-muted-foreground">
                          The people who currently match the trigger conditions
                          will enter your campaign.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div
                    className={cn(
                      'p-2 border rounded-md cursor-pointer transition-colors',
                      !includeExistingMatches
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:bg-muted/50'
                    )}
                    onClick={() => setIncludeExistingMatches(false)}
                  >
                    <div className="flex items-start space-x-2">
                      <input
                        type="radio"
                        checked={!includeExistingMatches}
                        onChange={() => setIncludeExistingMatches(false)}
                        className="mt-0.5 accent-primary border-border focus:ring-0"
                      />
                      <div>
                        <p className="text-xs font-medium text-foreground">
                          Future additions only
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Only people who match trigger conditions after the
                          campaign has started will enter your campaign.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <p className="text-xs text-gray-500">
              People will exit the campaign early if they stop matching the
              trigger.
            </p>
          </div>
        </ReviewSection>

        {/* Messages Section */}
        <ReviewSection
          icon={<Send className="w-4 h-4 text-success" />}
          title="Messages"
          description="Messages will be sent to: All subscribed people"
          additionalText="Message limit is OFF"
          onEdit={() => {
            // TODO: Implement edit messages functionality
            console.log('Edit messages');
          }}
        />

        {/* Workflow Section */}
        <ReviewSection
          icon={<Users className="w-4 h-4 text-success" />}
          title="Workflow"
          description="3 messages sending to all subscribed and unsubscribed. Sending to unsubscribed customers can have consequences."
          onEdit={() => {
            // TODO: Implement edit workflow functionality
            console.log('Edit workflow');
          }}
        >
          <p className="text-xs text-gray-500">
            <span className="underline cursor-pointer hover:text-gray-700">
              can have consequences
            </span>
          </p>
        </ReviewSection>

        {/* Goal Section */}
        <ReviewSection
          icon={<Target className="w-4 h-4 text-success" />}
          title="Goal"
          description="No goal set."
          onEdit={() => {
            // TODO: Implement edit goal functionality
            console.log('Edit goal');
          }}
        />

        {/* Exit Section */}
        <ReviewSection
          icon={<LogOut className="w-4 h-4 text-success" />}
          title="Exit"
          description="A person will exit the campaign early when they stop matching the trigger."
          onEdit={() => {
            // TODO: Implement edit exit functionality
            console.log('Edit exit');
          }}
        />

        {/* Footer Actions */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={loading}
            size="small"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleStartCampaign}
            disabled={loading}
            className="min-w-[120px]"
            size="small"
          >
            {loading ? (
              <div className="flex items-center">
                <Loader2 className="animate-spin -ml-1 mr-2 h-3 w-3 text-white" />
                Starting...
              </div>
            ) : (
              'Start Campaign'
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ReviewCampaignModal;
